import React, { useState, useEffect } from "react";
import { Card, CardBody, Row, Col } from "reactstrap";
import PanelHeader from "components/panelheader/panelHeader";
import { getTorneos } from "services/torneosService";
import { usePageLoading } from "hooks/usePageLoading";

// Estadísticas del torneo "anual" del año en curso, agrupadas por zona. Una
// misma zona puede repetirse en varias ruedas: se acumulan por nombre de zona.
const computeStatsPorZona = (t) => {
  // Roster por equipo del torneo, para contar los jugadores de cada zona.
  const jugadoresPorEquipo = {};
  (t.equipos || []).forEach((e) => {
    jugadoresPorEquipo[String(e.equipo)] = (e.listaFeTorneo || []).filter((r) => r.jugador).map((r) => String(r.jugador));
  });

  const zonas = [];
  const porNombre = {};

  (t.ruedas || []).forEach((ru) =>
    (ru.zonas || []).forEach((z) => {
      const nombre = z.nombre || "Sin nombre";
      let acc = porNombre[nombre];
      if (!acc) {
        acc = porNombre[nombre] = { nombre, partidos: 0, goles: 0, jugadores: new Set() };
        zonas.push(acc);
      }

      (z.equipos || []).forEach((eq) =>
        (jugadoresPorEquipo[String(eq)] || []).forEach((j) => acc.jugadores.add(j))
      );

      (z.fechas || []).forEach((f) =>
        (f.partidos || []).forEach((p) => {
          // El WO de los dos equipos no cuenta como partido jugado.
          if (p.jugado && p.wo !== "ambos") {
            acc.partidos++;
            acc.goles += (p.golesLocal || []).length + (p.golesVisitante || []).length;
          }
        })
      );
    })
  );

  return zonas.map((z) => ({
    nombre: z.nombre,
    partidos: z.partidos,
    goles: z.goles,
    jugadores: z.jugadores.size,
  }));
};

// Colores base de las tres cards (los de la primera zona).
const COLORES = ["#7b2d8e", "#b5006c", "#ba67ce"];

// Cada zona mantiene el color pero con el tono más bajo: se mezcla con blanco
// según la posición de la zona (tope en 0.6 para que el texto siga legible).
const bajarTono = (hex, nivel) => {
  const mezcla = Math.min(nivel * 0.18, 0.6);
  const n = parseInt(hex.slice(1), 16);
  const canal = (c) => Math.round(c + (255 - c) * mezcla);
  const r = canal((n >> 16) & 255);
  const g = canal((n >> 8) & 255);
  const b = canal(n & 255);
  return "rgb(" + r + ", " + g + ", " + b + ")";
};

const StatCard = ({ icon, label, value, bg }) => (
  <Col lg="4" sm="6">
    <Card className="dash-stat" style={{ backgroundColor: bg }}>
      <CardBody className="dash-stat-body">
        <div className="dash-stat-icon">
          <i className={icon} />
        </div>
        <div className="dash-stat-text">
          <div className="dash-stat-value">{value}</div>
          <div className="dash-stat-label">{label}</div>
        </div>
      </CardBody>
    </Card>
  </Col>
);

const Dashboard = (props) => {
  const { onTorneoLoaded } = props;
  const doneLoading = usePageLoading(props);
  const [loading, setLoading] = useState(true);
  const [torneo, setTorneo] = useState(null);
  const [zonas, setZonas] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const list = await getTorneos();
        const arr = Array.isArray(list) ? list : [];
        const year = new Date().getFullYear();
        const t = arr.find((x) => x.ano === year && (x.nombre || "").toLowerCase().includes("anual"));
        if (t) {
          setTorneo(t);
          setZonas(computeStatsPorZona(t));
          if (onTorneoLoaded) onTorneoLoaded(t.nombre + " · " + t.ano);
        }
      } catch (e) {
        console.error("Error cargando el dashboard:", e);
      } finally {
        setLoading(false);
        doneLoading();
      }
    })();
    return () => {
      if (onTorneoLoaded) onTorneoLoaded("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PanelHeader size="sm" />
      <div className="content">
        {loading ? (
          <p className="text-muted text-center py-5 my-0">Cargando estadísticas…</p>
        ) : !torneo ? (
          <Card>
            <CardBody>
              <p className="text-muted text-center py-4 my-0">
                No se encontró un torneo "anual" para el año {new Date().getFullYear()}.
              </p>
            </CardBody>
          </Card>
        ) : (
          <>
            <h5 className="mb-3" style={{ fontWeight: 700, color: "#1a202c" }}>
              {torneo.nombre} · {torneo.ano}
            </h5>
            {zonas.length === 0 ? (
              <Card>
                <CardBody>
                  <p className="text-muted text-center py-4 my-0">El torneo todavía no tiene zonas.</p>
                </CardBody>
              </Card>
            ) : (
              zonas.map((z, i) => (
                <React.Fragment key={z.nombre}>
                  <div className="section-title dash-zona-title">{z.nombre}</div>
                  <Row>
                    <StatCard
                      icon="flick icon-field-hockey"
                      label="Partidos jugados"
                      value={z.partidos}
                      bg={bajarTono(COLORES[0], i)}
                    />
                    <StatCard
                      icon="fas fa-bullseye"
                      label="Goles convertidos"
                      value={z.goles}
                      bg={bajarTono(COLORES[1], i)}
                    />
                    <StatCard
                      icon="fas fa-users"
                      label="Jugadores participantes"
                      value={z.jugadores}
                      bg={bajarTono(COLORES[2], i)}
                    />
                  </Row>
                </React.Fragment>
              ))
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Dashboard;
